import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Button,
  Input,
  Stack,
  Text,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  useToast,
  Flex,
  Checkbox,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { signUp } from '../services/apiService'
import { ChevronRightIcon } from '@chakra-ui/icons'
import Terms from '../components/terms'
import Privacy from '../components/privacy'
import { useLocation } from 'react-router-dom'
import {
  getPartnerFromURL,
  sanitizePartner,
  loadPartner,
  savePartner
} from '../utils/referral'

const SignUp = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()

  const location = useLocation()

  useEffect(() => {
    // 사용자가 signUp 페이지에 머무는 동안 URL이 변할 수도 있으니 한 번 더 동기화
    const fromUrl = sanitizePartner(getPartnerFromURL(location.search))
    if (fromUrl && fromUrl !== referrer) {
      setReferrer(fromUrl)
      savePartner(fromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const [loading, setLoading] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false) // 이용약관 동의 체크 상태
  const [agreePrivacy, setAgreePrivacy] = useState(false) // 개인정보처리방침 동의 체크 상태
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [modalTitle, setModalTitle] = useState('')
  const [modalContent, setModalContent] = useState<any>('')

  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [username, setUsername] = useState('')
  const [phone, setPhone] = useState('')

  const [referrer, setReferrer] = useState(() => {
    const fromUrl = sanitizePartner(getPartnerFromURL(window.location.search))
    if (fromUrl) {
      // URL로 들어온 값 즉시 저장
      savePartner(fromUrl)
      return fromUrl
    }
    return loadPartner()
  })

  // Dialog 열기 함수
  const handleOpenDialog = (type: string) => {
    if (type === 'terms') {
      setModalTitle(t('이용약관'))
      setModalContent(<Terms />)
    } else if (type === 'privacy') {
      setModalTitle(t('개인정보처리방침'))
      setModalContent(<Privacy />)
    }
    onOpen()
  }

  const handleRegister = async () => {
    if (!account || !password || !confirmPassword || !username || !phone) {
      toast({
        title: t('회원가입'),
        description: t('모든 필수 항목을 입력해주세요.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (account.length < 5) {
      toast({
        title: t('회원가입'),
        description: t('아이디는 5글자 이상이어야 합니다.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (password.length < 4) {
      toast({
        title: t('회원가입'),
        description: t('비밀번호는 4글자 이상이어야 합니다.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (username.length < 1) {
      toast({
        title: t('회원가입'),
        description: t('이름을 입력해주세요.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (password !== confirmPassword) {
      toast({
        title: t('회원가입'),
        description: t('비밀번호와 비밀번호 확인이 일치하지 않습니다.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (!agreeTerms) {
      toast({
        title: t('회원가입'),
        description: t('이용약관에 동의해주세요.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (!agreePrivacy) {
      toast({
        title: t('회원가입'),
        description: t('개인정보처리방침에 동의해주세요.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      setLoading(true) // 🔸로딩 시작

      const { data: res } = await signUp({
        account,
        password,
        username,
        phone,
        referrer
      })
      if (res.statusCode === 200) {
        // 성공 예시
        toast({
          title: t('회원가입'),
          description: t('{{name}}님, 환영합니다!', { name: username }),
          status: 'success',
          duration: 3000,
          isClosable: true
        })

        navigate('/signIn')
      }
    } catch (err: any) {
      toast({
        title: t('회원가입'),
        description:
          err?.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false) // 🔸로딩 종료
    }
  }

  return (
    <DefaultLayout>
      <Box
        minH="90vh"
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        pt={'30px'}
        bg="white"
        mb={'100px'}
      >
        <VStack spacing={8} w="full" maxW="md" px={6} textAlign="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              {t('회원가입')}
            </Text>
            <Text fontSize="sm" color="gray.600" mt={2}>
              {t('자동매매의 시작, 리플레이에 오신 것을 환영합니다!')}
              <br />
              {t('아래 정보를 입력 후 가입을 완료해주세요.')}
            </Text>
          </Box>

          <Stack spacing={4} w="full">
            <FormControl isRequired>
              <FormLabel>{t('아이디')}</FormLabel>
              <Input
                placeholder={t('아이디')}
                value={account}
                onChange={(e) => setAccount(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{t('비밀번호')}</FormLabel>
              <Input
                type="password"
                placeholder={t('비밀번호')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{t('비밀번호 확인')}</FormLabel>
              <Input
                type="password"
                placeholder={t('비밀번호 확인')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{t('이름')}</FormLabel>
              <Input
                placeholder={t('이름')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{t('핸드폰번호')}</FormLabel>
              <Input
                placeholder="01012345678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('추천인 아이디 (선택)')}</FormLabel>
              <Input
                placeholder={t('추천인 아이디')}
                value={referrer}
                onChange={(e) => setReferrer(e.target.value)}
              />
              <FormHelperText>
                {t('추천인이 있다면 입력해주세요')}
              </FormHelperText>
            </FormControl>

            {/* 이용약관 & 개인정보처리방침 동의 체크박스 */}
            <Stack spacing={3}>
              <Flex align="center" justify="space-between">
                <Checkbox
                  isChecked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                >
                  {t('이용약관에 동의합니다')}
                </Checkbox>
                <IconButton
                  icon={<ChevronRightIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleOpenDialog('terms')}
                  aria-label={t('약관 보기')}
                />
              </Flex>

              <Flex align="center" justify="space-between">
                <Checkbox
                  isChecked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                >
                  {t('개인정보처리방침에 동의합니다')}
                </Checkbox>
                <IconButton
                  icon={<ChevronRightIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleOpenDialog('privacy')}
                  aria-label={t('개인정보처리방침 보기')}
                />
              </Flex>
            </Stack>

            <Button
              colorScheme="purple"
              size="lg"
              onClick={handleRegister}
              isLoading={loading}
            >
              {t('회원가입')}
            </Button>
            <Button variant="link" onClick={() => navigate('/signIn')}>
              {t('이미 계정이 있으신가요? 로그인하기')}
            </Button>
          </Stack>
        </VStack>
      </Box>

      {/* 약관 내용 Dialog (Modal) */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalTitle}</ModalHeader>
          <ModalBody>
            <Text>{modalContent}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" onClick={onClose}>
              {t('닫기')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default SignUp
